import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PageHead from './../general/PageHead';
import { Skeleton } from './../general/Skeleton';
import { authStorage } from '../../utils/storage';
import { Search } from 'lucide-react';
import MapComponent from './../maps/MapComponent';
import StateLocals from './StateLocals';

function Towns() {
    const [groupedTowns, setGroupedTowns] = useState({});
    const [stateLocals, setStateLocals] = useState([]);
    const [townUnitCounts, setTownUnitCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const location = useLocation();
    const navigate = useNavigate();
    const letterRefs = useRef({});

    useEffect(() => {
        const token = authStorage.getToken();
        if (!token) { navigate("/login"); return; }
        setLoading(true);
        Promise.all([
            fetch(`/api/towns`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }),
            fetch(`/api/state-locals`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } })
        ])
            .then(async ([r1, r2]) => {
                if (r1.status === 401 || r2.status === 401) { authStorage.clearAuthData(); navigate("/login"); throw new Error("Unauthorized"); }
                const townsData = await r1.json();
                const stateLocalsData = await r2.json();
                const sortedTowns = townsData.sort((a, b) => a.Name.localeCompare(b.Name));
                const grouped = sortedTowns.reduce((acc, town) => {
                    const l = town.Name.charAt(0).toUpperCase();
                    if (!acc[l]) acc[l] = [];
                    acc[l].push(town);
                    return acc;
                }, {});
                // Build unit count map
                const counts = {};
                sortedTowns.forEach(t => { if (t.TownCount > 0) counts[t.Name] = t.TownCount; });
                setGroupedTowns(grouped);
                setStateLocals(stateLocalsData);
                setTownUnitCounts(counts);
                setLoading(false);
            })
            .catch(err => { console.error(err); setLoading(false); });
    }, [navigate]);

    useEffect(() => {
        if (location.state && location.state.scrollToLetter) {
            const letter = location.state.scrollToLetter;
            setTimeout(() => {
                if (letterRefs.current[letter]) {
                    letterRefs.current[letter].scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 300);
        }
    }, [location.state, groupedTowns, stateLocals]);

    const allTowns = useMemo(() => Object.values(groupedTowns).flat(), [groupedTowns]);
    const totalLocals = useMemo(() => allTowns.reduce((s, t) => s + (t.TownCount || 0), 0), [allTowns]);
    const topTowns = useMemo(() => Object.entries(townUnitCounts).sort((a,b) => b[1] - a[1]).slice(0, 5), [townUnitCounts]);
    const topTown = topTowns[0];
    const avgPerTown = allTowns.length > 0 ? (totalLocals / allTowns.length).toFixed(1) : '—';

    const filteredGrouped = useMemo(() => {
        if (!searchTerm) return groupedTowns;
        const q = searchTerm.toLowerCase();
        const out = {};
        Object.entries(groupedTowns).forEach(([letter, towns]) => {
            const filtered = towns.filter(t => t.Name.toLowerCase().includes(q));
            if (filtered.length > 0) out[letter] = filtered;
        });
        return out;
    }, [groupedTowns, searchTerm]);
    const filteredCount = useMemo(() => Object.values(filteredGrouped).reduce((s, arr) => s + arr.length, 0), [filteredGrouped]);

    const letters = Object.keys(groupedTowns);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <>
            <PageHead
                title="Towns"
                sub={`${allTowns.length || 169} towns covered in Connecticut`}
                actions={
                    <div className="search-box">
                        <Search size={14} className="ic" />
                        <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Find a town…" />
                    </div>
                }
            />

            {/* Map + Stats row */}
            <div className="grid-2-3 towns-overview" style={{ marginBottom: 28 }}>
                <div className="card">
                    <div className="card-head">
                        <h3>Statewide coverage</h3>
                        <div className="row-end tiny muted">CT &middot; All locals</div>
                    </div>
                    <div className="towns-map" style={{ overflow: 'hidden', borderRadius: '0 0 var(--radius-lg) var(--radius-lg)' }}>
                        <MapComponent zoom={9} center={[41.5, -72.7]} />
                    </div>
                </div>

                <div className="stack-md">
                    <div className="card card-pad">
                        <div style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--fg-2)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Quick stats</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                                <div style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.1 }}>{loading ? '—' : allTowns.length}</div>
                                <div className="tiny muted">Towns</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.1 }}>{loading ? '—' : totalLocals}</div>
                                <div className="tiny muted">Locals</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.1, color: 'var(--accent)' }}>{loading ? '—' : (topTown ? topTown[1] : '—')}</div>
                                <div className="tiny muted">Top: {topTown ? topTown[0] : '—'}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.1 }}>{loading ? '—' : avgPerTown}</div>
                                <div className="tiny muted">Avg per town</div>
                            </div>
                        </div>
                    </div>
                    <div className="card card-pad">
                        <div style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--fg-2)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Top 5 towns</div>
                        <div className="stack-sm">
                            {loading ? (
                                [...Array(5)].map((_, i) => <Skeleton key={i} height="24px" />)
                            ) : topTowns.map(([town, count], i) => (
                                <div key={town} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--bg-accent-soft)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 11, flexShrink: 0 }}>{i + 1}</div>
                                    <span style={{ fontSize: 13.5 }}>{town}</span>
                                    <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-2)' }}>{count} units</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Alpha jump nav */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 16 }}>
                {letters.map(l => (
                    <button key={l} onClick={() => scrollTo(l)} style={{ width: 32, height: 32, borderRadius: 6, background: 'var(--bg-sunken)', border: '1px solid var(--border-subtle)', fontSize: 12.5, fontWeight: 600, color: 'var(--fg-2)', cursor: 'pointer', transition: 'all 150ms' }}
                        onMouseEnter={e => { e.target.style.background = 'var(--bg-accent-soft)'; e.target.style.color = 'var(--accent)'; }}
                        onMouseLeave={e => { e.target.style.background = 'var(--bg-sunken)'; e.target.style.color = 'var(--fg-2)'; }}
                    >{l}</button>
                ))}
                {stateLocals.length > 0 && (
                    <button onClick={() => scrollTo('State')} style={{ padding: '0 12px', height: 32, borderRadius: 6, background: 'var(--accent)', border: 'none', fontSize: 12.5, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>State</button>
                )}
            </div>

            {/* Town directory */}
            <div className="card">
                <div className="card-head">
                    <h3>All towns</h3>
                    <span className="badge badge-neutral" style={{ marginLeft: 'auto' }}>{filteredCount}</span>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="grid-4 towns-state-grid" style={{ gap: 8 }}>
                            {[...Array(24)].map((_, i) => <Skeleton key={i} height="20px" width="80%" />)}
                        </div>
                    ) : (
                        <div className="towns-columns" style={{ columnCount: 4, columnGap: 32 }}>
                            {Object.entries(filteredGrouped).map(([letter, towns]) => (
                                <div key={letter} id={letter} ref={el => letterRefs.current[letter] = el} style={{ breakInside: 'avoid', marginBottom: 20 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', paddingBottom: 6, borderBottom: '1px solid var(--border-subtle)', marginBottom: 8 }}>{letter}</div>
                                    {towns.map((town, i) => (
                                        <div key={town.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 13.5 }}>
                                            <Link to={`/town/${town.Name}`} state={{ fromLetter: letter }} style={{ color: 'var(--fg-1)', textDecoration: 'none' }}
                                                onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                                                onMouseLeave={e => e.currentTarget.style.color = 'var(--fg-1)'}
                                            >{town.Name}</Link>
                                            {town.TownCount > 0 && <span style={{ fontSize: 11, color: 'var(--fg-3)', fontFamily: 'var(--font-mono)' }}>{town.TownCount}</span>}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}

                    {stateLocals.length > 0 && (
                        <div id="State" ref={el => letterRefs.current['State'] = el} style={{ marginTop: 28, borderTop: '2px solid var(--accent)', paddingTop: 20 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', paddingBottom: 6, borderBottom: '1px solid var(--border-subtle)', marginBottom: 10 }}>State Locals</div>
                            <StateLocals locals={stateLocals} />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default Towns;
